import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AxiosError } from 'axios';
import { AuthApi, AuthUser } from '../services';

type ProfileContextValue = {
  profile: AuthUser | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: { email?: string }) => Promise<AuthUser>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
};

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const responseMessage = (error.response?.data as { error?: string; message?: string } | undefined)?.error;
    const fallbackMessage = (error.response?.data as { message?: string } | undefined)?.message;
    return responseMessage ?? fallbackMessage ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AuthApi.getProfile();
      setProfile(response.profile);
    } catch (fetchError) {
      const message = parseErrorMessage(fetchError);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (data: { email?: string }) => {
    setLoading(true);
    setError(null);

    try {
      const response = await AuthApi.updateProfile(data);
      setProfile(response.profile);
      return response.profile;
    } catch (updateError) {
      const message = parseErrorMessage(updateError);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    setError(null);

    try {
      await AuthApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
    } catch (changeError) {
      const message = parseErrorMessage(changeError);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<ProfileContextValue>(
    () => ({
      profile,
      loading,
      error,
      fetchProfile,
      updateProfile,
      changePassword,
      clearError,
    }),
    [profile, loading, error, fetchProfile, updateProfile, changePassword, clearError],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
  const context = useContext(ProfileContext);

  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }

  return context;
};

